import {
    BORROWED_DRIVER_REASON,
    buildDriverFitOptions,
    driverUnavailableFor,
    groupFitOptions,
    isBorrowedDriver,
} from './driver-fit'
import {equipmentHasScheduleConflict} from './equipment-fit'
import {
    bookingFirstDay,
    equipmentCandidateReasons,
    equipmentMatchesRequirement,
    equipmentOnRentElsewhere,
    equipmentOutOfServiceFor,
} from './equipment-requirement-match'
import {
    bookingAvailabilityMessage,
    overlappingBoardBookings,
    resourceHasScheduleConflict,
} from './resource-schedule-conflict'
import {bulkEquipmentCandidateId, isStockEquipmentRequirement} from './stock-equipment'
import type {
    BookingDays,
    DispatchResource,
    DispatchTimelineData,
    DriverFitOption,
    JobAllocation,
    JobRequirement,
    RequirementKind,
    UnassignedJob,
} from './types'

export type ResourceCandidate = {
    resourceId: string
    resourceName: string
    kind: RequirementKind
    reasons?: string[]
    group?: DriverFitOption['group']
    equipmentTier?: 'matching' | 'other'
    /** Matching units booked on an overlapping job — shown after assignable options. */
    bookedElsewhere?: boolean
    conflictJob?: {jobId: string; jobName: string}
    conflictRelease?: {date: string; time: string}
    availabilityMessage?: string
    /** Stock equipment is assigned by quantity delta, not instance id. */
    stockQuantity?: boolean
    rentalAssetTypeId?: string
}

export function buildWorkerFitOptions(
    draftJob: UnassignedJob,
    data: DispatchTimelineData,
    bookingDays?: BookingDays,
    resources: DispatchResource[] = data.resources,
): DriverFitOption[] {
    if (!draftJob.span) {
        return buildUnscheduledWorkerOptions(draftJob, resources, bookingDays)
    }
    return buildDriverFitOptions({
        job: draftJob,
        resources,
        data,
        bookingDays,
    })
}

export function buildCandidatesByRequirement(
    draftJob: UnassignedJob,
    data: DispatchTimelineData,
    bookingDays?: BookingDays,
): Map<string, ResourceCandidate[]> {
    const groupedByType = new Map<string, Record<'recommended' | 'available', DriverFitOption[]>>()
    function groupedWorkersFor(requirement: JobRequirement) {
        const key = requirement.workerTypeId ?? ''
        let grouped = groupedByType.get(key)
        if (!grouped) {
            const ofType = workerTypeFilter(requirement, data.resources)
            grouped = groupFitOptions(
                buildWorkerFitOptions(
                    draftJob,
                    data,
                    bookingDays,
                    data.resources.filter((resource) => ofType(resource.id)),
                ),
            )
            groupedByType.set(key, grouped)
        }
        return grouped
    }

    return new Map(
        draftJob.requirements.map((requirement) => [
            requirement.id,
            buildCandidatesForRequirement(
                draftJob,
                requirement,
                requirement.kind === 'worker'
                    ? groupedWorkersFor(requirement)
                    : {recommended: [], available: []},
                data,
                bookingDays,
            ),
        ]),
    )
}

function resolveAllocationDisplayName(
    allocation: JobAllocation,
    workers: DispatchResource[],
    equipment: DispatchResource[],
): string {
    const fromPool =
        allocation.kind === 'worker'
            ? workers.find((worker) => worker.id === allocation.resourceId)?.name
            : equipment.find((asset) => asset.id === allocation.resourceId)?.name

    if (fromPool) {
        return fromPool
    }

    if (allocation.resourceName && allocation.resourceName !== allocation.resourceId) {
        return allocation.resourceName
    }

    return allocation.resourceName || allocation.resourceId
}

function assignedResourceCandidates(
    job: UnassignedJob,
    requirement: JobRequirement,
    workers: DispatchResource[],
    equipment: DispatchResource[],
): ResourceCandidate[] {
    return job.allocations
        .filter((allocation) => allocation.requirementId === requirement.id)
        .map((allocation) => {
            const resourceName = resolveAllocationDisplayName(allocation, workers, equipment)
            return {
                resourceId: allocation.resourceId,
                resourceName,
                kind: requirement.kind,
                equipmentTier:
                    requirement.kind === 'rental_asset'
                        ? equipmentMatchesRequirement(requirement, {
                              name: resourceName,
                              rentalAssetTypeId: equipment.find(
                                  (asset) => asset.id === allocation.resourceId,
                              )?.rentalAssetTypeId,
                              rentalAssetTypeName: equipment.find(
                                  (asset) => asset.id === allocation.resourceId,
                              )?.rentalAssetTypeName,
                          })
                            ? 'matching'
                            : 'other'
                        : undefined,
            }
        })
}

function mergeAssignedAvailableAndBooked(
    assigned: ResourceCandidate[],
    available: ResourceCandidate[],
    booked: ResourceCandidate[],
): ResourceCandidate[] {
    const assignedIds = new Set(assigned.map((candidate) => candidate.resourceId))
    const free = available.filter((candidate) => !assignedIds.has(candidate.resourceId))
    const freeIds = new Set(free.map((candidate) => candidate.resourceId))
    const bookedOnly = booked.filter(
        (candidate) => !assignedIds.has(candidate.resourceId) && !freeIds.has(candidate.resourceId),
    )
    return [...assigned, ...free, ...bookedOnly]
}

function buildCandidatesForRequirement(
    job: UnassignedJob,
    requirement: JobRequirement,
    groupedWorkers: Record<'recommended' | 'available', DriverFitOption[]>,
    data: DispatchTimelineData,
    bookingDays?: BookingDays,
): ResourceCandidate[] {
    const assigned = assignedResourceCandidates(job, requirement, data.resources, data.equipment)

    if (requirement.kind === 'worker') {
        const ofType = workerTypeFilter(requirement, data.resources)
        return mergeAssignedAvailableAndBooked(
            assigned,
            buildAvailableWorkerCandidates(groupedWorkers, job, requirement).filter((candidate) =>
                ofType(candidate.resourceId),
            ),
            buildBookedWorkerCandidates(job, data, bookingDays).filter((candidate) =>
                ofType(candidate.resourceId),
            ),
        )
    }

    return mergeAssignedAvailableAndBooked(
        assigned,
        buildAvailableEquipmentCandidates(data, job, requirement, bookingDays),
        buildBookedMatchingEquipmentCandidates(data, job, requirement, bookingDays),
    )
}

function workerTypeFilter(
    requirement: JobRequirement,
    resources: DispatchTimelineData['resources'],
): (resourceId: string) => boolean {
    if (!requirement.workerTypeId) {
        return () => true
    }

    const typeById = new Map(resources.map((resource) => [resource.id, resource.workerTypeId]))
    return (resourceId) => {
        const workerTypeId = typeById.get(resourceId)
        return workerTypeId == null || workerTypeId === requirement.workerTypeId
    }
}

function primaryConflictJob(
    resourceId: string,
    job: UnassignedJob,
    data: DispatchTimelineData,
):
    | {
          job: {jobId: string; jobName: string}
          release?: {date: string; time: string}
          availabilityMessage: string
      }
    | undefined {
    const booking = overlappingBoardBookings(resourceId, job, data)[0]
    if (!booking) {
        return undefined
    }
    return {
        job: {jobId: booking.jobId, jobName: booking.jobName},
        release: booking.release,
        availabilityMessage: bookingAvailabilityMessage(booking),
    }
}

function buildBookedWorkerCandidates(
    job: UnassignedJob,
    data: DispatchTimelineData,
    bookingDays?: BookingDays,
): ResourceCandidate[] {
    if (job.span == null) {
        return []
    }

    const allocatedIds = new Set(job.allocations.map((entry) => entry.resourceId))
    return data.resources
        .filter(
            (resource) =>
                !resource.outOfService &&
                !driverUnavailableFor(resource, job, bookingDays) &&
                !allocatedIds.has(resource.id) &&
                resourceHasScheduleConflict(resource.id, job, data),
        )
        .map((resource) => {
            const conflictJob = primaryConflictJob(resource.id, job, data)
            return {
                resourceId: resource.id,
                resourceName: resource.name,
                kind: 'worker' as const,
                bookedElsewhere: true,
                conflictJob: conflictJob?.job,
                conflictRelease: conflictJob?.release,
                availabilityMessage: conflictJob?.availabilityMessage,
                reasons: conflictJob
                    ? [`On ${conflictJob.job.jobName}`, conflictJob.availabilityMessage]
                    : ['Booked on another job'],
            }
        })
}

function buildBookedMatchingEquipmentCandidates(
    data: DispatchTimelineData,
    job: UnassignedJob,
    requirement: JobRequirement,
    bookingDays?: BookingDays,
): ResourceCandidate[] {
    if (job.span == null || isStockEquipmentRequirement(requirement, data.equipment)) {
        return []
    }

    const allocatedIds = new Set(job.allocations.map((entry) => entry.resourceId))
    const firstDay = bookingFirstDay(job, bookingDays)
    return data.equipment
        .filter(
            (asset) =>
                !equipmentOutOfServiceFor(asset, firstDay) &&
                !equipmentOnRentElsewhere(asset, job, data) &&
                !allocatedIds.has(asset.id) &&
                equipmentMatchesRequirement(requirement, asset) &&
                equipmentHasScheduleConflict(asset.id, job, data),
        )
        .map((asset) => {
            const conflictJob = primaryConflictJob(asset.id, job, data)
            return {
                resourceId: asset.id,
                resourceName: asset.name,
                kind: requirement.kind,
                equipmentTier: 'matching' as const,
                bookedElsewhere: true,
                conflictJob: conflictJob?.job,
                conflictRelease: conflictJob?.release,
                availabilityMessage: conflictJob?.availabilityMessage,
                reasons: conflictJob
                    ? [`On ${conflictJob.job.jobName}`, conflictJob.availabilityMessage]
                    : ['Booked on another job'],
            }
        })
}

function buildUnscheduledWorkerOptions(
    job: UnassignedJob,
    resources: DispatchTimelineData['resources'],
    bookingDays?: BookingDays,
): DriverFitOption[] {
    const allocatedIds = new Set(job.allocations.map((entry) => entry.resourceId))
    const available = resources.filter(
        (resource) =>
            !resource.outOfService &&
            !driverUnavailableFor(resource, job, bookingDays) &&
            !allocatedIds.has(resource.id),
    )

    const ordered = [
        ...available.filter((resource) => !isBorrowedDriver(resource, job)),
        ...available.filter((resource) => isBorrowedDriver(resource, job)),
    ]
    return ordered.map((resource, index) => ({
        driverId: resource.id,
        driverName: resource.name,
        // Without a schedule window we can't score conflicts — still surface a short
        // Recommended set so the picker isn't only "Also available".
        group: index < 3 ? ('recommended' as const) : ('available' as const),
        reasons: [
            ...(isBorrowedDriver(resource, job) ? [BORROWED_DRIVER_REASON] : []),
            'No schedule conflict check — time TBD',
        ],
    }))
}

function buildAvailableWorkerCandidates(
    grouped: Record<'recommended' | 'available', DriverFitOption[]>,
    job: UnassignedJob,
    requirement: JobRequirement,
): ResourceCandidate[] {
    const allocatedIds = new Set(job.allocations.map((entry) => entry.resourceId))
    const options = [...grouped.recommended, ...grouped.available].filter(
        (option) => !allocatedIds.has(option.driverId),
    )

    return options.map((option) => ({
        resourceId: option.driverId,
        resourceName: option.driverName,
        kind: requirement.kind,
        reasons: option.reasons,
        group: option.group,
    }))
}

function buildAvailableEquipmentCandidates(
    data: DispatchTimelineData,
    job: UnassignedJob,
    requirement: JobRequirement,
    bookingDays?: BookingDays,
): ResourceCandidate[] {
    const equipment = data.equipment

    if (isStockEquipmentRequirement(requirement, equipment)) {
        const rentalAssetTypeId = requirement.rentalAssetTypeId!
        return [
            {
                resourceId: bulkEquipmentCandidateId(rentalAssetTypeId),
                resourceName: requirement.label,
                kind: requirement.kind,
                equipmentTier: 'matching',
                stockQuantity: true,
                rentalAssetTypeId,
            },
        ]
    }

    const allocatedIds = new Set(job.allocations.map((entry) => entry.resourceId))
    const firstDay = bookingFirstDay(job, bookingDays)
    const available = equipment.filter(
        (asset) =>
            !equipmentOutOfServiceFor(asset, firstDay) &&
            !equipmentOnRentElsewhere(asset, job, data) &&
            !allocatedIds.has(asset.id) &&
            !equipmentHasScheduleConflict(asset.id, job, data),
    )

    const matchingAvailable = available.filter((asset) =>
        equipmentMatchesRequirement(requirement, asset),
    )

    return matchingAvailable.map((asset) => ({
        resourceId: asset.id,
        resourceName: asset.name,
        kind: requirement.kind,
        reasons: equipmentCandidateReasons(asset),
        equipmentTier: 'matching' as const,
    }))
}
